import React from 'react';
import ReactDOM from 'react-dom';
import { tsv,  timeFormatDefaultLocale, queue } from 'd3';
import App from './App';
import { createEmbed, loadStylesheet } from './embed'
import './polyfills.js'
import './index.css';

// For every locale you want to support, start by copying its json file
// from `node_modules\d3-time-format\locale` to `src\locale` and then import it below
import enUS from './locale/en-US.json'
import deDE from './locale/de-DE.json'
import itIT from './locale/it-IT.json'
import frFR from './locale/fr-FR.json'
import frCA from './locale/fr-CA.json'
import enGB from './locale/en-GB.json'
import jaJP from './locale/ja-JP.json'

const locales = {
  "en-US": enUS,
  "de-DE": deDE,
  "it-IT": itIT,
  "fr-FR": frFR,
  "fr-ca": frCA,
  "en-GB": enGB,
  "ja-JP": jaJP,
}

const { lang } = window

/*
  When embedding this app using a script tag, the following params can be supplied:

    view:                    The view type to instantiate. Can be: performance | holdings | sectors | premDisc
    fundInfo:                The name of the variable (defined somewhere in the embedding page) that contains fund info. If ommitted, defaults to 'fundInfo'
    fundMarketData:          The name of the variable (defined somewhere in the embedding page) that contains market data (for performance view). If ommitted, defaults to 'fundMarketData'
    fundBenchmarks:          The name of the variable (defined somewhere in the embedding page) that contains benchmarks info (for performance view). If ommitted, defaults to 'fundBenchmarks'
    hideVolumeGraph:         Optionally hide the volume graph. Default is false (don't hide)
    hideMarketOption:        Optionally hide the "MARKET" selector option. Default is false (don't hide)
    hideMarketVsNavSelector: Optionally hide the "MARKET | NAV" selector. Default is false (don't hide)
    hideG10kFootnote:        Optionally hide the (i) and info bubble for Growth of 10K. Default is false (don't hide)
    g10kFootnoteTextId:      The id inside i18n.tsv of the text to present inside the info bubble for Growth of 10K. Default is 'INFO_G10K'

  Example:
    <script src='/static/js/bundle.js?view=performance&hideVolumeGraph=true&hideMarketOption=true&hideG10kFootnote=true ></script>

  The params you specify become available to this app via the `player` object passed to the function below
*/

createEmbed('bundle', function(player /* an object w/ params passed via script tag */, app) {
  // This app gets its data from global variables defined on the page that embeds it.
  // So, if the page has a javascript line `window.fundInfo = { ... }` that data can be
  // obtained from here, using `window['fundInfo']`. Because we don't want to hardcode
  // the names of these global variables (because, for example, in Canada there are multiple funds
  // on a single page whose data is passed in via separate varaibles) we allow the names of those variables
  // to be specified via the embed tag, and we look for them in the `player object`.
  // If fundInfo=... is passed as an embed param, look for a variable with that name, otherwise default to a variable named 'fundInfo' 
  const fundInfo = window[player.fundInfo || 'fundInfo']

  const q = queue()
    .defer(tsv, process.env.PUBLIC_URL + '/i18n.tsv')
    .defer(loadStylesheet, process.env.PUBLIC_URL + '/static/css/bundle.css')
    .awaitAll(function(error, [i18nCsv, locale]) {
      const i18n = {}
      i18nCsv.forEach(term => i18n[term.id] = term)

      timeFormatDefaultLocale(locales[lang] || locales['en-US'])
      ReactDOM.render(
        !fundInfo
        ? <h1>Missing fundInfo object in DOM</h1>
        : <App 
            view={player.view} 
            fundTicker={fundInfo.symbol} 
            fundName={fundInfo.name}
            i18n={i18n} 
            lang={lang}
            embedOptions={player}
          />,
        player.view ? player.el : document.getElementById('root')
      )
    })
})



// WEBPACK FOOTER //
// ./src/index.js